import React from 'react';

/*
 * The code in this file was derived from npmjs.com/package/fuzzy but altered
 * because we need to be able to do safe pre/post prefix wrapping with React.
 * The package as written requires you use an options.pre and options.post as
 * strings which are then only converted back to React if you use the
 * __dangerouslySetInnerHtml option.
 *
 * ---
 *
 * Fuzzy
 * https://github.com/myork/fuzzy
 *
 * Copyright (c) 2012 Matt York
 * Licensed under the MIT license.
 */

/**
 * Does `pattern` fuzzy match `str`?
 */
export const test = (pattern: string, str: string) => {
  return match(pattern, str) !== null;
};

/**
 * Return all elements of `array` that have a fuzzy match against `pattern`.
 */
export const simpleFilter = (pattern: string, array: string[]) => {
  return array.filter((str) => {
    return test(pattern, str);
  });
};

/**
 * If `ptrn` matches `str`, wrap each matching character
 * in a <b /> and <u /> tag.
 */
export const match = (
  ptrn: string,
  str: string,
  opts?: {
    caseSensitive?: boolean;
    elem: JSX.Element;
  },
) => {
  let patternI = 0;
  let score = 0;
  let currScore = 0;
  const rendered: React.ReactNode[] = [];
  // String to compare against. This might be a lowercase version of the
  // raw string
  const compareString = (opts?.caseSensitive && str) || str.toLowerCase();
  const pattern = (opts?.caseSensitive && ptrn) || ptrn.toLowerCase();

  // For each character in the string, either add it to the result
  // or wrap in template if it's the next string in the pattern
  for (let i = 0; i < compareString.length; i++) {
    if (compareString[i] === pattern[patternI]) {
      rendered.push(
        opts?.elem
          ? React.cloneElement(opts?.elem, opts.elem.props, str[i])
          : React.createElement('b', {}, React.createElement('u', {}, str[i])),
      );
      patternI += 1;

      // consecutive characters should increase the score more than linearly
      currScore += 1 + currScore;
    } else {
      rendered.push(str[i]);
      currScore = 0;
    }
    score += currScore;
  }

  // return rendered string if we have a match for every char
  if (patternI === pattern.length) {
    return {
      rendered,
      // if the string is an exact match with pattern, totalScore should be maxed
      score: compareString === pattern ? Infinity : score,
    };
  }

  return null;
};

export interface FilterResult<T> {
  rendered: React.ReactNode;
  score: number;
  index: number;
  original: T;
}

/**
 * The normal entry point. Filters `arr` for matches against `ptrn`.
 * It returns an array with matching values of the type:
 *
 *  [{
 *    rendered:   b<b><u>lah</u></b> // The rendered component
 *    index:    2                    // The index of the element in `arr`
 *    original: 'blah'               // The original element in `arr`
 *    score: 10                      // The match score of this element
 *  }]
 */
export function filter<T>(
  pattern: string,
  arr: T[],
  extract?: (i: T) => string,
): FilterResult<T>[] {
  if (!arr || arr.length === 0) return [];

  return (
    arr
      .reduce((filtered: FilterResult<T>[], element, index) => {
        const str = extract ? extract(element) : element;

        const isMatch = typeof str === 'string' ? match(pattern, str) : null;
        if (isMatch) {
          filtered.push({
            rendered: isMatch.rendered,
            score: isMatch.score,
            index,
            original: element,
          });
        }
        return filtered;
      }, [])
      // Sort by score. Browsers are inconsistent wrt stable/unstable
      // sorting, so force stable by using the index in the case of tie.
      // See http://ofb.net/~sethml/is-sort-stable.html
      .sort((a, b) => {
        const compare = b.score - a.score;
        if (compare) return compare;
        return a.index - b.index;
      })
  );
}
